/**
 * Stage 1 — Meet the Letter.
 *
 * Static presentation: show each of the 4 forms (isolated, beginning,
 * middle, end) one at a time with the letter's audio played on entry.
 * Swipe/tap forward. Always earns 1 star on completion.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { api } from '../lib/api';
import { playUrl, unlockAudio, letterNameUrl } from '../lib/audio';
import { refreshMap } from '../state/content';
import { Spinner } from '../components/Spinner';
import { Celebration } from '../components/Celebration';
import { NavButton } from '../components/NavButton';
import type { LetterSummary } from '../state/content';
import { currentUser } from '../state/user';

const FORM_LABELS: Array<[keyof LetterSummary, string, string]> = [
  ['form_isolated', 'مُنْفَرِدٌ', 'Isolated'],
  ['form_beginning', 'فِي البِدَايَةِ', 'At the start'],
  ['form_middle', 'فِي الوَسَطِ', 'In the middle'],
  ['form_end', 'فِي النِّهَايَةِ', 'At the end'],
];

const MIN_FORM_MS = 1500; // minimum engagement per form before Next enables

export function StageMeet() {
  const { route } = useLocation();
  const { params } = useRoute();
  const letterId = Number(params.id);
  const [letter, setLetter] = useState<LetterSummary | null>(null);
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(false);
  const [canAdvance, setCanAdvance] = useState(false);

  useEffect(() => {
    api<{ ok: true; letter: LetterSummary }>(`/api/letters/${letterId}`).then(
      (r) => {
        setLetter(r.letter);
        unlockAudio();
        // Play the letter NAME (e.g. "أَلِف") — this is the "Meet the
        // Letter" introduction; the short vowel sound is for stage 3.
        setTimeout(() => void playUrl(letterNameUrl(r.letter.id)), 300);
      },
    );
  }, [letterId]);

  // Reset the "Next" gate each time we change forms — encourages the child
  // to look at each form for at least MIN_FORM_MS before moving on.
  useEffect(() => {
    setCanAdvance(false);
    const t = setTimeout(() => setCanAdvance(true), MIN_FORM_MS);
    return () => clearTimeout(t);
  }, [idx]);

  if (!letter) {
    return (
      <div class="flex-1 grid place-items-center">
        <Spinner />
      </div>
    );
  }

  const [key, labelAr, labelEn] = FORM_LABELS[idx]!;
  const form = letter[key] as string;
  const isLast = idx === FORM_LABELS.length - 1;

  async function complete() {
    if (currentUser.value) {
      try {
        await api('/api/progress', {
          method: 'POST',
          body: {
            letterId,
            stage: 1,
            stars: 1,
            attempts: 1,
            errors: [],
          },
        });
        void refreshMap();
      } catch {
        // Non-fatal — we still let the child proceed.
      }
    }
    setDone(true);
    setTimeout(() => route(`/island/${letterId}`), 1600);
  }

  if (done) {
    return <Celebration stars={1} subtitleEn="+1 star earned" />;
  }

  return (
    <div class="flex-1 flex flex-col items-center justify-between px-4 py-6 pb-10 max-w-md mx-auto w-full">
      <header class="w-full flex items-center justify-between">
        <NavButton ar="← إِنْهَاءٌ" en="Exit" onClick={() => route(`/island/${letterId}`)} />
        <div class="flex gap-1">
          {FORM_LABELS.map((_, i) => (
            <span
              key={i}
              class={`h-2 w-8 rounded-full ${i <= idx ? 'bg-coral' : 'bg-ink/15'}`}
            />
          ))}
        </div>
      </header>

      <div class="flex-1 flex flex-col items-center justify-center gap-6">
        <p class="text-base font-display text-ink/60">{labelAr}</p>
        <p class="text-xs text-ink/40" dir="ltr">
          {labelEn}
        </p>
        <p
          class="font-display text-coral select-none"
          style={{
            fontSize: 'clamp(140px, 48vw, 300px)',
            lineHeight: '1.15',
            paddingTop: '0.08em',
          }}
        >
          {form}
        </p>
        <button
          type="button"
          onClick={() => void playUrl(letterNameUrl(letter.id))}
          class="min-h-[64px] px-8 py-3 rounded-2xl bg-ocean text-white shadow-lg shadow-ocean/30 active:scale-95 flex flex-col items-center"
        >
          <span class="text-lg font-display">🔊 اِسْمَعْ</span>
          <span class="text-[10px] opacity-80" dir="ltr">Listen</span>
        </button>
      </div>

      <button
        type="button"
        disabled={!canAdvance}
        onClick={() => (isLast ? void complete() : setIdx((i) => i + 1))}
        class={`w-full min-h-[64px] px-8 py-3 rounded-2xl text-white shadow-xl flex flex-col items-center transition
                ${canAdvance ? 'bg-coral shadow-coral/40 active:scale-95' : 'bg-coral/40 shadow-none cursor-wait'}`}
      >
        <span class="text-xl font-display">
          {canAdvance ? (isLast ? 'أَتْمَمْتُ!' : 'التَّالِي') : '…'}
        </span>
        <span class="text-[10px] opacity-85" dir="ltr">
          {canAdvance ? (isLast ? "I'm done!" : 'Next') : 'Take a moment…'}
        </span>
      </button>
    </div>
  );
}
